<template>
  <!-- A labelled input with a leading icon (sign-in redesign). A real <label>,
       16px text so iOS does not zoom, and the focus ring in the tenant primary. -->
  <div class="si-field-wrap">
    <div class="si-field-wrap__labels">
      <label :for="id" class="si-label">{{ label }}</label>
      <slot name="label-action" />
    </div>
    <div class="si-field">
      <svg class="si-field__icon" viewBox="0 0 24 24" aria-hidden="true">
        <template v-if="icon === 'mail'">
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="m3 7 9 6 9-6" />
        </template>
        <template v-else-if="icon === 'lock'">
          <rect x="4" y="10" width="16" height="11" rx="2" />
          <path d="M8 10V7a4 4 0 0 1 8 0v3" />
        </template>
        <template v-else-if="icon === 'user'">
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21a8 8 0 0 1 16 0" />
        </template>
        <template v-else>
          <rect x="3" y="6" width="18" height="12" rx="2" />
          <path d="M7 12h.01M11 12h.01M15 12h.01" />
        </template>
      </svg>
      <input
        :id="id"
        ref="input"
        class="si-field__input"
        :type="type"
        :value="modelValue"
        :autocomplete="autocomplete"
        :inputmode="inputmode"
        :placeholder="placeholder"
        :disabled="disabled"
        :autofocus="autofocus"
        @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, type PropType } from "vue";

export default defineComponent({
  name: "IconField",
  props: {
    id: { type: String, required: true },
    label: { type: String, required: true },
    modelValue: { type: String, default: "" },
    type: { type: String, default: "text" },
    icon: { type: String as PropType<"mail" | "lock" | "user" | "code">, default: "mail" },
    autocomplete: { type: String, default: "off" },
    inputmode: {
      type: String as PropType<"text" | "email" | "numeric" | undefined>,
      default: undefined,
    },
    placeholder: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: false },
  },
  emits: ["update:modelValue"],
  setup(props) {
    const input = ref<HTMLInputElement | null>(null);
    // The `autofocus` attribute works only on page load; a step change mounts this later.
    onMounted(() => {
      if (props.autofocus) input.value?.focus();
    });
    return { input };
  },
});
</script>

<style scoped>
.si-field-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.si-field-wrap__labels {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
}
.si-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--sh-text);
}
.si-field {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.22);
  background: rgb(var(--v-theme-surface));
  transition:
    box-shadow 0.15s ease,
    border-color 0.15s ease;
}
.si-field:focus-within {
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 0 0 4px rgba(var(--v-theme-primary), 0.18);
}
.si-field__icon {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  fill: none;
  stroke: var(--sh-text-60);
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.si-field__input {
  flex: 1;
  min-width: 0;
  min-height: 44px;
  border: 0;
  outline: none;
  background: transparent;
  /* 16px: iOS Safari zooms the page into any input smaller than this. */
  font: inherit;
  font-size: 16px;
  color: var(--sh-text);
}
.si-field__input::placeholder {
  color: var(--sh-text-45);
}
.si-field__input:disabled {
  opacity: 0.6;
}
</style>
